import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardListStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, FormatUtils, PaletteUtils, PermissionUtils, RoutePaths } from '@shared/utils';
import { CardStatus } from '@shared/api';
import { CardItem } from '../cardItem';
import { View } from '@widgets';
import { Pagination, Select, Table, TableCellType, Text } from '@shared/widgets';

interface CardsProps {
  className?: string;
  title?: ReactNode;
  loading?: boolean;
  listType?: boolean;
  currency?: string;
  accountId?: number;
  accountIds?: number[];
  userId?: string;
}

export const Cards = (props: CardsProps) => {
  const { navigate } = useRouteHook();

  const cardListStore = useUnit($cardListStore.store);

  const [status, setStatus] = useState<CardStatus>();
  const cardsState = useListState({ list: 'cards' });

  const { t } = useTranslation('accounts');

  useEffect(() => {
    return () => $cardListStore.events.clear();
  }, []);

  useEffect(() => {
    cardsState.onChangePage(1);
  }, [props.accountIds, props.currency]);

  useEffect(() => {
    if (cardListStore.loading || cardsState.page <= 1) return;
    if (cardListStore.data?.data?.length) return;
    cardsState.onChangePage(1);
  }, [cardListStore.loading, cardListStore.data, cardsState.page]);

  useEffect(() => {
    if (props.loading) return;
    $cardListStore.events.getCards({
      AccountId: props.accountId,
      AccountIds: props.accountIds,
      AssignedUserId: props.userId,
      Currency: props.currency,
      Status: status,
      Count: cardsState.count,
      FromIndex: cardsState.fromIndex,
      ReturnTotalCount: true,
    });
  }, [props.loading, props.accountId, props.accountIds, props.userId, props.currency, status, cardsState]);

  const statusOptions = useMemo(
    () => Object.entries(CardStatus).map(([value, label]) => ({ label: t(`statuses.card.${label}`, { ns: 'common' }), value: value as CardStatus })),
    [t],
  );
  const filteredCards = useMemo(() => {
    if (!props.accountIds) return cardListStore.data?.data || [];
    return cardListStore.data?.data?.filter((card) => props.accountIds?.includes(card.accountId)) || [];
  }, [cardListStore.data?.data, props.accountIds]);
  const columns = useMemo(
    () => [
      t('card.relatedCardColumns.cardholder'),
      t('card.relatedCardColumns.card'),
      t('card.relatedCardColumns.type'),
      t('card.relatedCardColumns.product'),
      t('card.relatedCardColumns.status'),
      t('card.relatedCardColumns.account'),
      t('card.relatedCardColumns.currency'),
    ],
    [t],
  );
  return (
    <section
      className={cn(
        'flex flex-col gap-6 pb-12',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <div className='flex items-center justify-between'>
        {props.title && (
          <Text weight='semibold' size={PaletteUtils.isInsigniaPalette ? '2xl' : 'lg'}>
            {props.title}
          </Text>
        )}
        <Select
          className='ml-auto min-w-[150px]'
          nullValue={{ isEnable: true, text: t('placeholders.allStatuses', { ns: 'common' }) }}
          items={statusOptions}
          value={status}
          onChange={setStatus}
        />
      </div>
      <View
        loading={props.loading || cardListStore.loading}
        error={cardListStore.error}
        onRefresh={() =>
          $cardListStore.events.getCards({
            AccountId: props.accountId,
            AccountIds: props.accountIds,
            AssignedUserId: props.userId,
            Currency: props.currency,
            Status: status,
            Count: cardsState.count,
            FromIndex: cardsState.fromIndex,
            ReturnTotalCount: true,
          })
        }
        noData={!filteredCards.length}
      >
        <div className='flex flex-col gap-6'>
          {props.listType && (
            <Table
              isScrollable
              columns={columns}
              items={filteredCards.map((card) => [
                {
                  type: TableCellType.Custom,
                  value: (
                    <div className='flex items-center gap-3'>
                      <Text
                        className='flex h-8 w-8 items-center justify-center rounded-full bg-primary'
                        weight='medium'
                        size='xl'
                        color={PaletteUtils.getColor('black', 'white')}
                      >
                        {card.cardHolder?.split('')[0]}
                      </Text>
                      <Text truncate size='sm'>
                        {card.cardHolder}
                      </Text>
                    </div>
                  ),
                },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.cardNumber) },
                { type: TableCellType.Text, value: t(`card.type.${card.cardType.toLowerCase()}`) },
                { type: TableCellType.Text, value: card.productDisplayName },
                { type: TableCellType.Text, value: t(`statuses.card.${card.status}`, { ns: 'common' }) },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.accountNumber) },
                { type: TableCellType.Text, value: card.currency.toUpperCase() },
              ])}
              isRowHoverable={(rowIndex) => PermissionUtils.viewCardDetails(filteredCards[rowIndex].cardId)}
              onClick={(rowIndex) => {
                const cardId = filteredCards[rowIndex].cardId;
                const viewCardDetails = PermissionUtils.viewCardDetails(cardId);
                if (viewCardDetails === false) return;
                navigate(RoutePaths.card.onNavigate(cardId));
              }}
            />
          )}
          {!props.listType && (
            <div
              className={cn(
                'rounded-tr-2 rounded-br-2 rounded-bl-2 flex w-full flex-wrap gap-x-[26px] gap-y-[30px] rounded-tl-none bg-white pb-[30px] dark:bg-soft-black',
                'max-md:justify-center',
                PaletteUtils.isInsigniaPalette && 'pt-5',
              )}
            >
              {filteredCards.map((card) => {
                const viewCardDetails = PermissionUtils.viewCardDetails(card.cardId);
                return (
                  <Link
                    key={card.cardId}
                    to={RoutePaths.card.onNavigate(card.cardId)}
                    onClick={(e) => viewCardDetails === false && e.preventDefault()}
                  >
                    <CardItem card={card} withHover />
                  </Link>
                );
              })}
            </div>
          )}
          <Pagination
            perPageLabel={props.listType ? undefined : t('pagination.perPage.items', { ns: 'common' })}
            totalItemCount={cardListStore.data?.total || 0}
            currentPage={cardsState.page}
            countItemPage={cardsState.count}
            onChangePage={cardsState.onChangePage}
            onChangeCountItemPage={cardsState.onChangeCount}
          />
        </div>
      </View>
    </section>
  );
};
